import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock, Plus, Sparkles } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type {
  ReleaseDetail,
  ReleaseObservation,
  ReleaseValidationCheck,
} from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  InlineAlert,
  LoadingState,
  ProgressBar,
  StatusBadge,
} from "../../shared/ui";
import { ValidationCheckDrawer } from "./ValidationCheckDrawer";
import { ObservationDrawer } from "./ObservationDrawer";
import { useReleaseMutation } from "./useReleaseMutation";
import {
  VALIDATION_STATUS_LABELS,
  VALIDATION_STATUS_TONE,
  describeObservation,
} from "./releaseVocabulary";

/**
 * §40 and §41: did it actually work, and has anybody watched it long enough?
 *
 * <p>Kept apart from the execution tab deliberately. Those steps say what was done; these say
 * whether it worked, and a deployment can run every step perfectly while having broken something.
 * A release that could be finished from the execution log alone would let exactly that be signed
 * off by somebody reading the wrong list.
 */
export function ReleaseValidationTab({
  release,
  onChanged,
}: {
  release: ReleaseDetail;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [editing, setEditing] = useState<ReleaseValidationCheck | null>(null);
  const [adding, setAdding] = useState(false);
  const [settingWindow, setSettingWindow] = useState(false);
  const { run, saving } = useReleaseMutation(release.id);

  const checks = useQuery({
    queryKey: ["release-validation", release.id],
    queryFn: () =>
      api.get<ReleaseValidationCheck[]>(`/api/v1/releases/${release.id}/validation`),
  });

  const observation = useQuery({
    queryKey: ["release-observation", release.id],
    queryFn: () => api.get<ReleaseObservation>(`/api/v1/releases/${release.id}/observation`),
  });

  const rows = checks.data ?? [];
  const mandatory = rows.filter((row) => row.mandatory);
  const settled = mandatory.filter(
    (row) => row.status === "PASSED" || row.status === "SKIPPED",
  ).length;
  const failed = rows.filter((row) => row.status === "FAILED").length;

  const addSuggested = () =>
    void run(() => api.post(`/api/v1/releases/${release.id}/validation/suggested`), onChanged);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Observation window"
          subtitle="§41: how long this is watched before it can be called finished."
          action={
            can("RELEASE_EDIT") && (
              <Button size="sm" variant="secondary" onClick={() => setSettingWindow(true)}>
                Set window
              </Button>
            )
          }
        />
        <CardBody>
          <p className="flex items-center gap-2 text-sm text-ink-800">
            <Clock className="h-4 w-4 text-ink-400" aria-hidden />
            {observation.data
              ? describeObservation(
                  observation.data.completeAt,
                  observation.data.complete,
                )
              : "—"}
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Post-deployment validation"
          subtitle={
            rows.length === 0
              ? "No checks planned yet."
              : `${settled} of ${mandatory.length} mandatory checks settled`
          }
          action={
            can("RELEASE_EDIT") && rows.length > 0 && (
              <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
                Add check
              </Button>
            )
          }
        />
        <CardBody className="space-y-3">
          {mandatory.length > 0 && (
            <ProgressBar
              value={settled}
              total={mandatory.length}
              tone={failed > 0 ? "red" : settled === mandatory.length ? "green" : "amber"}
              label="Mandatory checks"
            />
          )}

          {failed > 0 && (
            <InlineAlert tone="error">
              {failed} {failed === 1 ? "check has" : "checks have"} failed. Deployment finishing is
              not the same as the release having worked — this release cannot be completed until
              they are put right or waived.
            </InlineAlert>
          )}

          {checks.isPending ? (
            <LoadingState label="Loading checks…" />
          ) : rows.length === 0 ? (
            <EmptyState
              title="No checks yet"
              message="Is the application healthy, do the core journeys work, is the error rate normal? These are what say the release actually worked."
              action={
                can("RELEASE_EDIT") ? (
                  <div className="flex gap-2">
                    <Button icon={<Sparkles className="h-4 w-4" />} loading={saving}
                      onClick={addSuggested}>
                      Add the usual checks
                    </Button>
                    <Button variant="secondary" onClick={() => setAdding(true)}>
                      Add one
                    </Button>
                  </div>
                ) : undefined
              }
            />
          ) : (
            <ol className="divide-y divide-ink-100">
              {rows.map((row) => (
                <li key={row.id} className="flex items-start justify-between gap-3 py-2.5">
                  <div className="flex min-w-0 gap-2.5">
                    <span className="tnum mt-0.5 w-5 shrink-0 text-xs text-ink-400">
                      {row.sequenceNo}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-900">
                        {row.checkName}
                        {!row.mandatory && (
                          <span className="ml-2 text-xs font-normal text-ink-500">optional</span>
                        )}
                      </p>
                      <p className="mt-0.5 text-xs text-ink-500">
                        {row.category.replace(/_/g, " ").toLowerCase()}
                        {row.owner && ` · ${row.owner.fullName}`}
                        {row.completedBy && ` · ${row.completedBy.fullName}`}
                      </p>
                      {row.failureReason && (
                        <p className="mt-1 rounded-md border-l-2 border-red-300 bg-red-50 px-2 py-1 text-xs text-red-800">
                          {row.failureReason}
                        </p>
                      )}
                      {row.result && !row.failureReason && (
                        <p className="mt-1 text-xs text-ink-600">{row.result}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <StatusBadge tone={VALIDATION_STATUS_TONE[row.status]}>
                      {VALIDATION_STATUS_LABELS[row.status]}
                    </StatusBadge>
                    {can("RELEASE_EDIT") && (
                      <Button variant="ghost" size="sm" onClick={() => setEditing(row)}>
                        Record
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </CardBody>
      </Card>

      <ValidationCheckDrawer
        release={release}
        check={editing}
        open={adding || editing != null}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={onChanged}
      />
      <ObservationDrawer
        release={release}
        observation={observation.data ?? null}
        open={settingWindow}
        onClose={() => setSettingWindow(false)}
        onSaved={onChanged}
      />
    </div>
  );
}
